import React, { useEffect, useState } from 'react';
import { api } from './AdminApp';
import Modal from './Modal';
import Toggle from './Toggle';
import ImageField from './ImageField';
import Loader from '../Components/Loader/Loader';

const EMPTY = {
  name: '',
  designation: '',
  description: '',
  featured: false,
  experience_years: '',
  cases_count: '',
  display_order: 0,
  is_active: true,
};

export default function Doctors() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null); // null | {id:0,...} for new
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const d = await api('/admin/doctors');
      setList(d.doctors || []);
      setError('');
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const startNew = () => setEditing({ ...EMPTY, id: 0 });

  const startEdit = (doc) =>
    setEditing({
      id: doc.id,
      name: doc.name || '',
      designation: doc.designation || '',
      description: doc.description || '',
      featured: Boolean(doc.featured),
      experience_years: doc.experience_years || '',
      cases_count: doc.cases_count || '',
      display_order: doc.display_order ?? 0,
      is_active: Boolean(doc.is_active),
      image_url: doc.image_url || null,
    });

  const save = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    Object.entries(editing).forEach(([k, v]) => {
      // Booleans must serialize as '1'/'' — appending raw false becomes the
      // STRING "false", which the backend's truthiness check reads as true.
      if (typeof v === 'boolean') fd.append(k, v ? '1' : '');
      else fd.append(k, v);
    });
    if (file) fd.append('image', file);
    try {
      await api('/admin/doctors/save', { method: 'POST', body: fd });
      setEditing(null);
      setFile(null);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this doctor?')) return;
    try {
      await api('/admin/doctors/delete', {
        method: 'POST',
        body: JSON.stringify({ id }),
      });
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const toggle = async (id) => {
    try {
      await api('/admin/doctors/toggle', {
        method: 'POST',
        body: JSON.stringify({ id }),
      });
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const set = (k) => (e) =>
    setEditing({
      ...editing,
      [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
    });

  const removeImage = async () => {
    await api('/admin/doctors/delete-image', {
      method: 'POST',
      body: JSON.stringify({ id: editing.id }),
    });
    setEditing((prev) => ({ ...prev, image_url: null }));
    load();
  };

  const close = () => {
    setEditing(null);
    setFile(null);
  };

  return (
    <div>
      <div className="admin-page-head">
        <h1 className="admin-page-title">Doctors</h1>
        <button className="admin-btn admin-btn-primary" onClick={startNew}>
          + Add doctor
        </button>
      </div>
      {error && <div className="admin-alert admin-alert-error">{error}</div>}

      {editing && (
        <Modal
          title={editing.id ? `Edit doctor #${editing.id}` : 'New doctor'}
          onClose={close}
        >
          <form className="admin-form" onSubmit={save}>
            <div className="admin-form-grid">
            <label>
              Name *
              <input value={editing.name} onChange={set('name')} required />
            </label>
            <label>
              Designation
              <input value={editing.designation} onChange={set('designation')} />
            </label>
            <label>
              Experience (e.g. 12+)
              <input value={editing.experience_years} onChange={set('experience_years')} />
            </label>
            <label>
              Cases count
              <input value={editing.cases_count} onChange={set('cases_count')} />
            </label>
            <label>
              Display order
              <input type="number" value={editing.display_order} onChange={set('display_order')} />
            </label>
            <div className="admin-form-full">
              <ImageField
                label="Photo"
                url={editing.image_url}
                file={file}
                onPick={(f) => setFile(f)}
                onRemove={removeImage}
                hint="Doctor photo · JPEG, PNG, WebP · up to 10 MB"
              />
            </div>
            <label style={{ gridColumn: '1 / -1' }}>
              Description
              <textarea rows={3} value={editing.description} onChange={set('description')} />
            </label>
            <div className="admin-form-checks" style={{ gridColumn: '1 / -1' }}>
              <label>
                <input type="checkbox" checked={editing.featured} onChange={set('featured')} />
                Featured on homepage
              </label>
              <label>
                <input type="checkbox" checked={editing.is_active} onChange={set('is_active')} />
                Visible on site
              </label>
            </div>
            <div className="admin-form-actions" style={{ gridColumn: '1 / -1' }}>
              <button type="submit" className="admin-btn admin-btn-primary">
                Save
              </button>
              <button
                type="button"
                className="admin-btn"
                onClick={close}
              >
                Cancel
              </button>
            </div>
          </div>
          </form>
        </Modal>
      )}

      {loading ? (
        <Loader inline status="Loading doctors" />
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Photo</th>
              <th>Name</th>
              <th>Designation</th>
              <th>Order</th>
              <th>Featured</th>
              <th>Visible</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.map((d) => (
              <tr key={d.id}>
                <td>{d.id}</td>
                <td>
                  {d.image_url ? (
                    <img className="admin-thumb" src={d.image_url} alt={d.name} />
                  ) : (
                    '—'
                  )}
                </td>
                <td>{d.name}</td>
                <td>{d.designation}</td>
                <td>{d.display_order}</td>
                <td>{d.featured ? '★' : ''}</td>
                <td>
                  <Toggle checked={d.is_active} onChange={() => toggle(d.id)} />
                </td>
                <td className="admin-actions">
                  <button className="admin-btn admin-btn-sm" onClick={() => startEdit(d)}>
                    Edit
                  </button>
                  <button
                    className="admin-btn admin-btn-sm admin-btn-danger"
                    onClick={() => remove(d.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
